import { CSSProperties, useEffect, useRef, useState } from 'react'
import { responsiveBreakpointMap } from '~/constants'
import { COLORS } from '~/constants/colors'
import { useGetDeviceWidth, useOnClickOutside } from '~/hooks'
import { Spacer } from '../Common/Spacer'
import Icon from '../Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { SidebarListSubitem } from './SidebarListSubitem'

interface SidebarListItemProps {
    /**
     * List item text
     */
    children: string
    /**
     * List item icon
     */
    icon?: JSX.Element
    /**
     * List item badge
     */
    badge?: JSX.Element
    /**
     * Squeeze list item
     */
    shrink?: boolean
    /**
     * Responsive breakpoint
     */
    responsive?: keyof typeof responsiveBreakpointMap
    /**
     * List item active state
     */
    active?: boolean
    /**
     * List of subitem
     */
    subitem?: JSX.Element[]
    /**
     * Optional click event
     */
    onClick?: () => void
}

export const SidebarListItem = ({
    children,
    icon,
    badge,
    shrink = false,
    responsive = 'sm',
    active = false,
    subitem = [],
    onClick
}: SidebarListItemProps) => {
    const wrapper = useRef<HTMLLIElement>(null)
    const [showSubitem, setShowSubitem] = useState(false)
    const [subitemStyle, setSubitemStyle] = useState<CSSProperties>({})
    const [deviceWidth, setDeviceWidth] = useState(0)

    useGetDeviceWidth(setDeviceWidth, () => {
        handleClickOutside()
    })

    const isResponsiveView = deviceWidth !== 0 && deviceWidth <= responsiveBreakpointMap[responsive]

    const isShrinkedView = !isResponsiveView && shrink

    useEffect(() => {
        setShowSubitem(false)
        setSubitemStyle({})
    }, [isShrinkedView])

    const handleClick = () => {
        if (!!subitem.length) {
            if (isShrinkedView) {
                toggleShowSubitem()
                return
            }
            setShowSubitem(!showSubitem)
            return
        }

        onClickEvent()
    }

    const onClickEvent = () => {
        if (!onClick) {
            return
        }

        onClick()
    }

    const toggleShowSubitem = () => {
        setShowSubitem(true)

        if (!showSubitem) {
            if (!wrapper) {
                return
            }
            const element = wrapper.current

            if (!element) {
                return
            }

            const rect = element.getBoundingClientRect()
            const top = rect.top
            const isRTL = typeof document !== 'undefined' && document.documentElement.dir === 'rtl'

            if (isRTL) {
                const right = window.innerWidth - rect.left
                setSubitemStyle({
                    top,
                    right,
                    position: 'fixed',
                    width: 200,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    borderRadius: '0.5rem',
                    padding: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid ' + COLORS.MAIN.NEUTRALS[200]
                })
            } else {
                const left = rect.right
                setSubitemStyle({
                    top,
                    left,
                    position: 'fixed',
                    width: 200,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    borderRadius: '0.5rem',
                    padding: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid ' + COLORS.MAIN.NEUTRALS[200]
                })
            }
        }
    }

    const handleClickOutside = () => {
        if (isShrinkedView) {
            setShowSubitem(false)
            setSubitemStyle({})
        }
    }

    useOnClickOutside(wrapper, handleClickOutside)

    const content = (
        <a
            className={`flex no-underline items-center px-2 text-sm leading-normal relative font-normal ${active
                ? 'text-[#FF5200] bg-[#FF52001A] hover:text-[#FF5200]'
                : 'text-neutrals-900 hover:bg-[#FF52001A]'
                } ${isShrinkedView ? 'py-2 justify-center flex-col text-center' : 'py-2 rounded-md'}`}
            role="button"
            onClick={() => handleClick()}>
            {isShrinkedView && active && (
                <div className="h-8 w-1 bg-[#FF5200] ml-1 rtl:ml-0 rtl:mr-1 rounded-r-sm rtl:rounded-r-none rtl:rounded-l-sm absolute left-0 rtl:left-auto rtl:right-0 top-1/2 transform -translate-y-1/2"></div>
            )}
            {!!icon && <span className={isShrinkedView ? 'mb-1' : 'mr-3 rtl:mr-0 rtl:ml-3 inline-flex items-center'}>{icon}</span>}
            {isShrinkedView && (
                <span className="text-[8px] leading-[10px] text-center font-medium truncate w-full px-0.5">
                    {children}
                </span>
            )}
            {!shrink ? (
                <>
                    <span>{children}</span>
                    <Spacer></Spacer>
                </>
            ) : null}
            {!!badge && (
                <span className={isShrinkedView ? 'absolute flex top-1 right-1.5 rtl:right-auto rtl:left-1.5 w-5 h-5' : 'mr-1 rtl:mr-0 rtl:ml-1'}>{badge}</span>
            )}
            {!isShrinkedView && !!subitem.length && (
                <span
                    className="flex items-center justify-center"
                    role="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        setShowSubitem(!showSubitem)
                    }}>
                    <Icon name={!showSubitem ? 'chevron-down-outline' : 'chevron-up-outline'} size="lg"></Icon>
                </span>
            )}
        </a>
    )

    return (
        <li ref={wrapper}>
            {isShrinkedView ? (
                <TooltipProvider delayDuration={100}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            {content}
                        </TooltipTrigger>
                        <TooltipContent side={typeof document !== 'undefined' && document.documentElement.dir === 'rtl' ? 'left' : 'right'} sideOffset={5} className="z-[1050]">
                            <span>{children}</span>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ) : (
                content
            )}
            {!!subitem.length && (!isShrinkedView || (isShrinkedView && showSubitem)) && (
                <ul
                    className={`grid gap-2 grid-cols-1 ${isShrinkedView && showSubitem ? '' : 'pt-2'} ${!isShrinkedView && !showSubitem && 'hidden'
                        }`}
                    style={subitemStyle}
                    onClick={(e) => {
                        e.stopPropagation()
                        handleClickOutside()
                    }}>
                    {isShrinkedView && (
                        <SidebarListSubitem isHeader={true} active={active} badge={badge} onClick={() => onClickEvent()}>
                            {children}
                        </SidebarListSubitem>
                    )}
                    {subitem}
                </ul>
            )}
        </li>
    )
}
